import type { VoidFunctionComponent } from 'react';

import { isFreeTier } from '../../../../shared/subscriptions/utils';
import { window } from '../../../globals';
import { getCategoryLabel } from '../../../helpers/issuesV2';
import Subnav from '../../generic/Subnav';
import SubnavCountBadge from '../../generic/Subnav/SubnavCountBadge';
import { issueOverviewFeature } from '../../settings/billing/features/consts';

import { useProjectIssuesSubnav } from './useProjectIssuesSubnav';

type Props = {
  baseRevisionURL: string;
  revisionScanId?: number;
  projectLocator: string;
  revisionId: string | null;
  isOverviewAvailable: boolean;
};

/**
 * ProjectSubnavItemIssues is the Issues dropdown subnav item for projects.
 */
const ProjectSubnavItemIssues: VoidFunctionComponent<Props> = ({
  baseRevisionURL,
  revisionScanId,
  projectLocator,
  revisionId,
  isOverviewAvailable,
}) => {
  const {
    issueCount,
    categorySummary,
    isCategorySummaryError,
    totalIssuesCount,
    permittedCategories,
    getProjectIssuesURL,
  } = useProjectIssuesSubnav({
    baseRevisionURL,
    revisionScanId,
    projectLocator,
    revisionId,
  });

  const isIssueOverviewRestricted = isFreeTier({
    plan: window.user?.organization?.stripe_plan,
    stripePriceLimit: window.user?.organization?.stripePriceLimit,
  });
  const categorySummaryUnavailableTitle = isCategorySummaryError ? 'Issue counts unavailable' : undefined;

  return (
    <Subnav.Dropdown to={`${baseRevisionURL}/issues`} navKey="issues">
      <Subnav.Dropdown.RootItem>
        Issues
        <SubnavCountBadge
          count={issueCount || 0}
          isLoading={issueCount === null && !isCategorySummaryError}
          isUnavailable={isCategorySummaryError}
          title={categorySummaryUnavailableTitle}
        />
      </Subnav.Dropdown.RootItem>
      {isOverviewAvailable && (
        <Subnav.Dropdown.PaidFeatureItem
          to={`${baseRevisionURL}/issues/overview`}
          navKey="issues/overview"
          isRestricted={isIssueOverviewRestricted}
          modalFocusFeature={issueOverviewFeature}
        >
          Overview
          {!isIssueOverviewRestricted && (
            <SubnavCountBadge
              count={totalIssuesCount || 0}
              isLoading={totalIssuesCount === null && !isCategorySummaryError}
              isUnavailable={isCategorySummaryError}
              title={categorySummaryUnavailableTitle}
            />
          )}
        </Subnav.Dropdown.PaidFeatureItem>
      )}
      {permittedCategories.map((category) => (
        <Subnav.Dropdown.Item key={category} to={getProjectIssuesURL(category)} navKey={`issues/${category}`}>
          {getCategoryLabel(category)}
          <SubnavCountBadge
            count={categorySummary?.[category] || 0}
            isLoading={!categorySummary && !isCategorySummaryError}
            isUnavailable={isCategorySummaryError}
            title={categorySummaryUnavailableTitle}
          />
        </Subnav.Dropdown.Item>
      ))}
    </Subnav.Dropdown>
  );
};

export default ProjectSubnavItemIssues;
